<template>
    <div>
        <!-- Action Buttons -->
        <div class="action-toolbar">
            <button @click="printBill" class="btn btn-primary">
                🖨️ Print Bill
            </button>
            <button @click="emailBillAsPDF" class="btn btn-success" :disabled="sendingEmail">
                {{ sendingEmail ? '📧 Sending...' : '📧 Email PDF' }}
            </button>
        </div>

        <!-- Bill Container - Fixed size for printing -->
        <div ref="billContent" class="bill-paper">
            <div class="bill-content">
                <!-- Company Header -->
                <div class="company-header">
                    <div class="company-logo" v-if="companyInfo.logo">
                        <img :src="companyInfo.logo" alt="Company Logo">
                    </div>
                    <div class="company-info">
                        <h1 class="company-name">{{ companyInfo.name }}</h1>
                        <p class="company-address">{{ companyInfo.address }}</p>
                        <p class="company-contact">
                            {{ companyInfo.phone }} | {{ companyInfo.email }}
                        </p>
                        <p class="company-tax">VAT/TIN: {{ companyInfo.taxId }}</p>
                    </div>
                    <div class="print-date">
                        <p>Date Printed: {{ printTimestamp }}</p>
                        <p>Time: {{ printTime }}</p>
                    </div>
                </div>

                <div class="divider"></div>

                <!-- Client Information -->
                <div class="client-section">
                    <div class="client-details">
                        <h3>Bill To:</h3>
                        <p class="client-name">{{ client.fullname_client }}</p>
                        <p class="client-account">Account #: {{ client.userpk_client }}</p>
                        <p class="client-address">{{ client.brgy_client }}</p>
                    </div>
                    <div class="bill-summary">
                        <div class="summary-item">
                            <span>Total Balance:</span>
                            <span class="amount">{{ formatCurrency(client.balance_client) }}</span>
                        </div>
                    </div>
                </div>

                <!-- Bills Table -->
                <table class="bills-table">
                    <thead>
                        <tr>
                            <th>Due Date</th>
                            <th>Period</th>
                            <th>Description</th>
                            <th class="text-right">Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        <!-- Previous Bills (Arrears) -->
                        <tr v-for="(bill, index) in previousBills" :key="index" class="arrears-row">
                            <td></td>
                            <td>{{ bill.userpk_te }}</td>
                            <td>{{ bill.description || 'Previous Unpaid Bill' }}</td>
                            <td class="text-right">{{ formatCurrency(bill.amount_te) }}</td>
                        </tr>

                        <!-- Current Bill -->
                        <tr v-if="currentBill" class="current-bill-row">
                            <td>{{ formatDate(currentBill.datedue_te) }}</td>
                            <td>{{ currentBill.transref_te }}</td>
                            <td><strong>Current Bill</strong></td>
                            <td class="text-right"><strong>{{ formatCurrency(currentBill.amount_te) }}</strong></td>
                        </tr>
                    </tbody>

                    <!-- Totals -->
                    <tfoot>
                        <tr class="total-row">
                            <td colspan="3" class="text-right">Subtotal (Arrears):</td>
                            <td class="text-right">{{ formatCurrency(arrearsTotal) }}</td>
                        </tr>
                        <tr class="total-row">
                            <td colspan="3" class="text-right">Current Bill:</td>
                            <td class="text-right">{{ formatCurrency(currentBillAmount) }}</td>
                        </tr>
                        <tr class="grand-total">
                            <td colspan="3" class="text-right">Total Amount Due:</td>
                            <td class="text-right">{{ formatCurrency(client.balance_client) }}</td>
                        </tr>
                    </tfoot>
                </table>

                <!-- Footer -->
                <div class="bill-footer">
                    <div class="payment-terms">
                        <p><strong>Payment Terms:</strong> Payment is due within 15 days of bill date</p>
                        <p><strong>Late Payment:</strong> A late fee of 2% per month will be applied to overdue balances
                        </p>
                    </div>
                    <div class="thank-you">
                        Thank you for your business!
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import html2pdf from 'html2pdf.js'
import Formatters from '@/utils/formatters'
//eslint-disable-next-line
const props = defineProps({
    client: {
        type: Object,
        required: true
    },
    bills: {
        type: Array,
        required: true // Array of bill objects with due_date, amount, period, etc.
    },
    companyInfo: {
        type: Object,
        default: () => ({
            name: 'EXODOUS I.T. SOLUTIONS',
            address: 'Sitio Bakilid, San Juan Palompon, Leyte',
            phone: '09703287224',
            email: 'exodoustechsolution@gmail.com',
            taxId: '267-517-358-00000',
            logo: 'https://admin.exodoustechsolutions.com/favicon.ico' // Optional logo URL
        })
    }
})
//eslint-disable-next-line
const emit = defineEmits(['email-sent'])

const billContent = ref(null)
const sendingEmail = ref(false)

// Timestamp
const printTimestamp = ref('')
const printTime = ref('')

// Computed properties for bills
const previousBills = computed(() => {
    // Filter out the most recent bill (current) and sort by due date
    return [{ amount_te: props.client.balance_client - props.client.monthlydue_client }]
})

const currentBill = computed(() => {
    const bills = props.bills
        .filter(bill => bill.module_te === 'BILLING')
        .sort((a, b) => new Date(b.duedate_te) - new Date(a.duedate_te));

    return bills.length > 0 ? bills[0] : null;
})
const arrearsTotal = computed(() => {
    return props.client.balance_client - props.client.monthlydue_client
})

const currentBillAmount = computed(() => {
    return currentBill.value ? currentBill.value.amount_te : 0
})

// Helper functions
const formatCurrency = (amount) => {
    return Formatters.formatCurrency(amount);
}

const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    })
}

// Set timestamp
const updateTimestamp = () => {
    const now = new Date()
    printTimestamp.value = now.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    })
    printTime.value = now.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    })
}

// Print bill
const printBill = () => {
    updateTimestamp()

    // Use setTimeout to ensure timestamp is updated
    setTimeout(() => {
        const printWindow = window.open('', '_blank')
        printWindow.document.write(`
      <html>
        <head>
          <title>Bill - ${props.client.fullname_client}</title>
          <style>
            @page {
              size: letter;
              margin: 0.5in;
            }
            body {
              font-family: Arial, sans-serif;
              line-height: 1.4;
              color: #333;
              width: 7.5in;
              margin: 0 auto;
            }
            .bill-content {
              padding: 20px;
            }
            .company-header {
              display: flex;
              justify-content: space-between;
              align-items: start;
              margin-bottom: 20px;
            }
            .company-name {
              font-size: 24px;
              margin: 0 0 5px 0;
              color: #000;
            }
            .company-logo img {
            max-height: 100px;
            max-width: 100px;
            }

            .company-details p {
              margin: 2px 0;
              font-size: 12px;
              color: #666;
            }
            .divider {
              border-top: 2px solid #333;
              margin: 15px 0;
            }
            .client-section {
              display: flex;
              justify-content: space-between;
              margin-bottom: 20px;
            }
            .client-name {
              font-size: 18px;
              font-weight: bold;
              margin: 5px 0;
            }
            .client-account {
              color: #666;
            }
            .bills-table {
              width: 100%;
              border-collapse: collapse;
              margin: 20px 0;
            }
            .bills-table th {
              background: #f0f0f0;
              padding: 10px;
              text-align: left;
              border-bottom: 2px solid #ddd;
            }
            .bills-table td {
              padding: 8px 10px;
              border-bottom: 1px solid #eee;
            }
            .text-right {
              text-align: right;
            }
            .arrears-row {
              background: #fff9f9;
            }
            .current-bill-row {
              background: #f0fff0;
              font-weight: bold;
            }
            .total-row td {
              padding: 5px 10px;
              border-bottom: 1px solid #ddd;
            }
            .grand-total {
              background: #f0f0f0;
              font-weight: bold;
              font-size: 16px;
            }
            .grand-total td {
              padding: 12px 10px;
              border-top: 2px solid #333;
            }
            .bill-footer {
              margin-top: 30px;
              padding-top: 20px;
              border-top: 1px dashed #ccc;
              font-size: 12px;
              color: #666;
            }
            .thank-you {
              text-align: center;
              margin-top: 20px;
              font-style: italic;
            }
            .print-date {
              text-align: right;
              font-size: 11px;
              color: #999;
            }
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          ${billContent.value.outerHTML}
        </body>
      </html>
    `)

        printWindow.document.close()
        printWindow.focus()
        // Wait for content to load then print
        setTimeout(() => {
            printWindow.print()
            printWindow.close();
        }, 250)
        printWindow.onafterprint = () => {
            printWindow.close()
        }
    }, 100)
}

// Email bill as PDF
const emailBillAsPDF = async () => {
    sendingEmail.value = true
    updateTimestamp()

    try {
        // Generate PDF
        const element = billContent.value
        const opt = {
            margin: [0.5, 0.5, 0.5, 0.5],
            filename: `bill_${props.client.account_number}_${Date.now()}.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2 },
            jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
        }

        const pdfBlob = await html2pdf().from(element).set(opt).outputPdf('blob')

        // Create form data for email
        const formData = new FormData()
        formData.append('pdf', pdfBlob, `bill_${props.client.account_number}.pdf`)
        formData.append('clientEmail', props.client.email)
        formData.append('clientName', props.client.fullname_client)
        formData.append('subject', `Bill Statement - ${props.client.fullname_client}`)

        // Send email
        const response = await fetch('/api/send-bill-email', {
            method: 'POST',
            body: formData
        })

        if (response.ok) {
            alert('Bill sent successfully to client email!')
            emit('email-sent', { success: true })
        } else {
            throw new Error('Failed to send email')
        }
    } catch (error) {
        console.error('Error sending email:', error)
        alert('Failed to send bill via email. Please try again.')
    } finally {
        sendingEmail.value = false
    }
}

onMounted(() => {
    updateTimestamp()
})
</script>

<style scoped>
.action-toolbar {
    margin-bottom: 20px;
    display: flex;
    gap: 10px;
}

.btn {
    padding: 10px 20px;
    border: none;
    border-radius: 5px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    transition: all 0.3s ease;
}

.btn-primary {
    background: #3b82f6;
    color: white;
}

.btn-primary:hover {
    background: #2563eb;
}

.btn-success {
    background: #10b981;
    color: white;
}

.btn-success:hover:not(:disabled) {
    background: #059669;
}

.btn-success:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

/* Fixed paper size */
.bill-paper {
    width: 8.5in;
    min-width: 8.5in;
    max-width: 8.5in;
    margin: 0 auto;
    background: white;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    overflow: hidden;
}

.bill-content {
    padding: 0.5in;
    font-family: 'Arial', sans-serif;
    color: #333;
    background: white;
}

.company-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 20px;
}

.company-logo img {
    max-height: 100px;
    max-width: 100px;
}

.company-name {
    font-size: 24px;
    font-weight: bold;
    margin: 0 0 5px 0;
    color: #000;
}

.company-address,
.company-contact,
.company-tax {
    margin: 2px 0;
    font-size: 11px;
    color: #666;
}

.print-date {
    text-align: right;
    font-size: 10px;
    color: #999;
}

.print-date p {
    margin: 2px 0;
}

.divider {
    border-top: 2px solid #333;
    margin: 15px 0;
}

.client-section {
    display: flex;
    justify-content: space-between;
    margin-bottom: 25px;
}

.client-details h3 {
    margin: 0 0 10px 0;
    font-size: 14px;
    color: #666;
}

.client-name {
    font-size: 18px;
    font-weight: bold;
    margin: 0 0 5px 0;
}

.client-account {
    color: #666;
    margin: 0 0 3px 0;
}

.client-address {
    margin: 0;
    font-size: 12px;
    color: #666;
}

.bill-summary {
    background: #f8f9fa;
    padding: 15px;
    border-radius: 4px;
}

.summary-item {
    display: flex;
    justify-content: space-between;
    gap: 20px;
}

.summary-item .amount {
    font-weight: bold;
    color: #059669;
}

.bills-table {
    width: 100%;
    border-collapse: collapse;
    margin: 20px 0;
    font-size: 12px;
}

.bills-table th {
    background: #f0f0f0;
    padding: 10px;
    text-align: left;
    border-bottom: 2px solid #ddd;
    font-weight: 600;
    font-size: 12px;
}

.bills-table td {
    padding: 8px 10px;
    border-bottom: 1px solid #eee;
}

.text-right {
    text-align: right;
}

.arrears-row {
    background: #fff9f9;
}

.arrears-row td {
    color: #b45309;
}

.current-bill-row {
    background: #f0fff0;
    font-weight: bold;
}

.current-bill-row td {
    color: #065f46;
}

.total-row td {
    padding: 5px 10px;
    border-bottom: 1px solid #ddd;
    font-size: 12px;
}

.grand-total {
    background: #f0f0f0;
    font-weight: bold;
    font-size: 14px;
}

.grand-total td {
    padding: 12px 10px;
    border-top: 2px solid #333;
}

.bill-footer {
    margin-top: 30px;
    padding-top: 20px;
    border-top: 1px dashed #ccc;
    font-size: 10px;
    color: #666;
}

.payment-terms p {
    margin: 3px 0;
}

.thank-you {
    text-align: center;
    margin-top: 20px;
    font-style: italic;
    color: #333;
    font-size: 12px;
}

/* Ensure fixed width on all devices */
@media screen and (max-width: 8.5in) {
    .bill-paper {
        width: 100%;
        min-width: 100%;
        overflow-x: auto;
    }

    .bill-content {
        width: 8.5in;
        min-width: 8.5in;
    }
}

@media print {
    .action-toolbar {
        display: none;
    }

    .bill-paper {
        box-shadow: none;
        width: 100%;
        min-width: 100%;
    }

    .bill-content {
        padding: 0.5in;
    }
}
</style>